import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Box, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MonitoredServer,
  MonitoredServersList,
  ReorderMonitoredServersRequest,
} from "@xop/dashboard-shared";
import { useEffect, useMemo, useState } from "react";

import { useCan } from "../../auth/permissions.js";
import { PageLoader } from "../../components/ui/page-loader.js";
import { api } from "../../lib/api.js";
import { countByHealth, matchesHealthFilter, type ServerHealthFilter } from "./serverHealth.js";
import { MONITORED_KEY } from "./server-list/monitoredKey.js";
import { ServerDetailsModal } from "./server-list/ServerDetailsModal.js";
import { EditServerModal } from "./server-list/ServerFormModals.js";
import { EmptyFilterState, EmptyState, ServerFilterBar } from "./server-list/ServerFilters.js";
import { SortableServerCard, StaticServerCard } from "./server-list/ServerCard.js";

/**
 * Server Status main panel — a responsive, sortable grid of server cards.
 *
 * This file is the **orchestrator** only: the monitored-servers query, the
 * optimistic local reorder + persist, the health filter, and the
 * details / edit modal wiring. The presentational pieces live as focused
 * modules under `server-list/`:
 *
 *   - `ServerCard`        — the card (sortable + static variants)
 *   - `ServerFilters`     — the All/Healthy/Broken filter bar + empty states
 *   - `ServerDetailsModal`— the read-only details dialog
 *   - `ServerFormModals`  — the Add + Edit dialogs (`AddServerModal` is
 *                            re-exported below so `ServerStatusPage` is unchanged)
 *   - `serverPills` / `DotMenu` / `serverHealth` — shared helpers
 *
 * Cards are sortable via dnd-kit; the new order is persisted server-side in
 * one PATCH at the end of each drop. The "Add server" trigger lives on the
 * page header (the parent passes `onAdd` so the empty-state can trigger it).
 */

// Re-exported so `ServerEventHistory` keeps importing it from `./ServerList.js`.
export { MONITORED_KEY } from "./server-list/monitoredKey.js";
// Re-exported so `ServerStatusPage` keeps importing it from `./ServerList.js`.
export { AddServerModal } from "./server-list/ServerFormModals.js";

/** Two cards per row keeps the URL legible inside the 70%-width left
 * column. We only step up to three on very wide screens where the URL
 * still has room to breathe; the old 3- and 4-col breakpoints truncated
 * `https://…` to a couple of glyphs. */
const GRID_SX = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr" },
} as const;

export function ServerList({
  onAdd,
}: {
  onAdd?: (() => void) | undefined;
} = {}): JSX.Element {
  const canManage = useCan("server:manage");
  const qc = useQueryClient();
  const [detailsForId, setDetailsForId] = useState<number | null>(null);
  const [editForId, setEditForId] = useState<number | null>(null);
  // All / Healthy / Broken view. Reordering is only offered in the "all"
  // view (a drag index over a filtered subset wouldn't map back to the
  // full persisted order).
  const [filter, setFilter] = useState<ServerHealthFilter>("all");
  // Local-first ids so drags feel instantaneous; server is the source
  // of truth on next refetch.
  const [orderedIds, setOrderedIds] = useState<number[] | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: MONITORED_KEY,
    queryFn: () =>
      api<MonitoredServersList>("/api/servers/monitored", {
        retryOnAuthError: true,
      }),
    refetchInterval: 15_000,
  });

  // Sync local order from the server payload, but don't fight an
  // in-flight optimistic reorder (we only re-seed when set sizes change
  // — i.e. an add/remove/initial load).
  useEffect(() => {
    if (!data) return;
    const serverIds = data.servers.map((s) => s.id);
    if (
      orderedIds === null ||
      orderedIds.length !== serverIds.length ||
      orderedIds.some((id) => !serverIds.includes(id))
    ) {
      setOrderedIds(serverIds);
    }
  }, [data, orderedIds]);

  const reorder = useMutation({
    mutationFn: (ids: number[]) =>
      api<void>("/api/servers/monitored/order", {
        method: "PATCH",
        body: { ids } satisfies ReorderMonitoredServersRequest,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MONITORED_KEY }),
  });

  // Resolve open Details / Edit rows from the latest list each render.
  const detailsLive = useMemo(
    () => data?.servers.find((s) => s.id === detailsForId) ?? null,
    [data, detailsForId],
  );
  const editLive = useMemo(
    () => data?.servers.find((s) => s.id === editForId) ?? null,
    [data, editForId],
  );

  const ordered = useMemo<MonitoredServer[]>(() => {
    if (!data) return [];
    if (!orderedIds) return data.servers;
    const byId = new Map(data.servers.map((s) => [s.id, s] as const));
    const out: MonitoredServer[] = [];
    for (const id of orderedIds) {
      const s = byId.get(id);
      if (s) out.push(s);
    }
    // Append any new server we don't have a position for yet.
    for (const s of data.servers) {
      if (!orderedIds.includes(s.id)) out.push(s);
    }
    return out;
  }, [data, orderedIds]);

  // Filter-pill counts come from the raw payload (stable regardless of
  // the active view); the rendered set respects the active filter.
  const counts = useMemo(() => countByHealth(data?.servers ?? []), [data]);
  const visible = useMemo(
    () => ordered.filter((s) => matchesHealthFilter(s, filter)),
    [ordered, filter],
  );
  const sortable = filter === "all";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id || !orderedIds) return;
    const from = orderedIds.indexOf(Number(active.id));
    const to = orderedIds.indexOf(Number(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(orderedIds, from, to);
    setOrderedIds(next);
    if (canManage) reorder.mutate(next);
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {isLoading && <PageLoader />}
      {isError && (
        <Typography sx={{ fontSize: 14, color: "error.main" }}>
          {error instanceof Error ? error.message : "Could not load servers"}
        </Typography>
      )}
      {data && ordered.length === 0 && !isLoading && (
        <EmptyState canManage={canManage} onAdd={onAdd} />
      )}

      {data && ordered.length > 0 && (
        <>
          <ServerFilterBar filter={filter} counts={counts} onChange={setFilter} />

          {visible.length === 0 ? (
            <EmptyFilterState filter={filter} />
          ) : sortable ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={visible.map((s) => s.id)}
                strategy={rectSortingStrategy}
              >
                <Box sx={GRID_SX}>
                  {visible.map((s) => (
                    <SortableServerCard
                      key={s.id}
                      server={s}
                      canManage={canManage}
                      onShowDetails={() => setDetailsForId(s.id)}
                      onEdit={() => setEditForId(s.id)}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          ) : (
            // Filtered views are a subset, so reordering is disabled —
            // render plain (non-sortable) cards.
            <Box sx={GRID_SX}>
              {visible.map((s) => (
                <StaticServerCard
                  key={s.id}
                  server={s}
                  canManage={canManage}
                  onShowDetails={() => setDetailsForId(s.id)}
                  onEdit={() => setEditForId(s.id)}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {detailsLive && (
        <ServerDetailsModal
          server={detailsLive}
          onClose={() => setDetailsForId(null)}
        />
      )}
      {editLive && (
        <EditServerModal
          server={editLive}
          onClose={() => setEditForId(null)}
        />
      )}
    </Box>
  );
}
